import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import EditableImage from "./EditableImage";
import {
  SEGMENT_ORDER,
  buildSegmentVisibilityChanges,
  getSegmentDefinition,
} from "../landingStudio/segmentRegistry";

const TABS = ["Apariencia", "Imágenes", "Secciones", "Datos y contacto", "SEO y publicación"];

function clone(value) {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function normalizePublicStatus(value) {
  const candidate = String(value || "").trim().toLowerCase();
  return ["draft", "published", "maintenance"].includes(candidate)
    ? candidate
    : "draft";
}

function publicStatusLabel(value) {
  const status = normalizePublicStatus(value);
  if (status === "published") return "Publicado";
  if (status === "maintenance") return "Mantenimiento";
  return "Borrador";
}

function withoutPublicStatus(value) {
  const next = clone(value || {});
  delete next.publicStatus;
  return next;
}

function statusConfirmationMessage(status) {
  if (status === "published") {
    return "Se guardará el borrador pendiente y se publicará su última revisión real. La web pública pasará a mostrar esa versión.";
  }
  if (status === "maintenance") {
    return "La web pública mostrará la página de mantenimiento usando la última versión publicada. El borrador y el historial no se perderán.";
  }
  return "La web pública quedará oculta como borrador. La última versión publicada y todo el historial se conservarán.";
}

function getAtPath(source, path, fallback = "") {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => value?.[/^\d+$/.test(key) ? Number(key) : key], source) ?? fallback;
}

function setAtPath(source, path, value) {
  const next = clone(source);
  const keys = String(path || "").split(".").filter(Boolean);
  let cursor = next;
  keys.forEach((rawKey, index) => {
    const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    const nextKey = /^\d+$/.test(keys[index + 1]) ? Number(keys[index + 1]) : keys[index + 1];
    if (cursor[key] === undefined || cursor[key] === null) cursor[key] = typeof nextKey === "number" ? [] : {};
    cursor = cursor[key];
  });
  return next;
}

function Field({ form, path, label, onChange, multiline = false, minRows = 1, type = "text", helperText = "" }) {
  return (
    <TextField
      fullWidth
      type={type}
      label={label}
      value={getAtPath(form, path, "")}
      onChange={(event) => onChange(path, event.target.value)}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      helperText={helperText}
    />
  );
}

function ColorField({ form, path, label, onChange }) {
  const value = getAtPath(form, path, "#000000") || "#000000";
  return (
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Box component="input" type="color" value={value} onChange={(event) => onChange(path, event.target.value)} sx={{ width: 48, height: 44, p: 0, border: 0, bgcolor: "transparent", cursor: "pointer" }} />
      <TextField fullWidth label={label} value={value} onChange={(event) => onChange(path, event.target.value)} />
    </Stack>
  );
}

const SECTION_GROUPS = Object.freeze([
  {
    id: "structure",
    title: "Estructura de la página",
    description: "Las piezas que ordenan el recorrido desde que alguien entra al sitio.",
  },
  {
    id: "commerce",
    title: "Productos y servicios",
    description: "Bloques que ayudan a mostrar y ordenar la propuesta comercial sin duplicar catálogos.",
  },
  {
    id: "content",
    title: "Contenido del negocio",
    description: "Secciones para explicar quién sos, por qué elegirte y cómo contactarte.",
  },
  {
    id: "closing",
    title: "Cierre y acceso rápido",
    description: "Elementos que acompañan el final del recorrido o quedan disponibles durante la navegación.",
  },
]);

function SegmentMiniPreview({ type = "cards", active = false }) {
  const ink = active ? "primary.main" : "rgba(25,36,47,.34)";
  const soft = active ? "rgba(243,90,58,.12)" : "rgba(25,36,47,.06)";
  const line = { height: 5, borderRadius: 99, bgcolor: ink };
  const card = { flex: 1, minWidth: 0, height: 34, borderRadius: 1.2, bgcolor: soft, border: "1px solid", borderColor: active ? "rgba(243,90,58,.22)" : "rgba(25,36,47,.10)" };

  if (type === "navigation") {
    return (
      <Stack spacing={1} sx={{ width: "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box sx={{ ...line, width: "30%", height: 8 }} />
          <Stack direction="row" spacing={0.6}>{[0, 1, 2].map((item) => <Box key={item} sx={{ ...line, width: 13, height: 4 }} />)}</Stack>
        </Stack>
        <Box sx={{ height: 38, borderRadius: 1.2, bgcolor: soft }} />
      </Stack>
    );
  }

  if (type === "hero") {
    return (
      <Stack spacing={0.8} sx={{ width: "100%" }}>
        <Box sx={{ ...line, width: "54%", height: 9 }} />
        <Box sx={{ ...line, width: "78%", opacity: 0.6 }} />
        <Box sx={{ ...line, width: "66%", opacity: 0.45 }} />
        <Box sx={{ width: 30, height: 11, borderRadius: 99, bgcolor: ink, mt: 0.4 }} />
      </Stack>
    );
  }

  if (type === "split" || type === "contact") {
    return (
      <Stack direction="row" spacing={1} sx={{ width: "100%", alignItems: "stretch" }}>
        <Box sx={{ ...card, height: 54 }} />
        <Stack spacing={0.7} sx={{ flex: 1.2, pt: 0.5 }}>
          <Box sx={{ ...line, width: "62%", height: 8 }} />
          <Box sx={{ ...line, width: "92%", opacity: 0.55 }} />
          <Box sx={{ ...line, width: "74%", opacity: 0.4 }} />
        </Stack>
      </Stack>
    );
  }

  if (type === "footer") {
    return (
      <Stack spacing={1} sx={{ width: "100%", justifyContent: "flex-end" }}>
        <Box sx={{ height: 25, borderRadius: 1.2, bgcolor: soft }} />
        <Stack direction="row" spacing={0.7}>
          {[0, 1, 2].map((item) => <Box key={item} sx={{ ...line, width: `${24 - item * 3}%`, opacity: 0.45 + item * 0.15 }} />)}
        </Stack>
      </Stack>
    );
  }

  if (type === "floating") {
    return (
      <Box sx={{ width: "100%", height: 58, position: "relative", borderRadius: 1.2, bgcolor: soft }}>
        <Box sx={{ position: "absolute", right: 7, bottom: 7, width: 22, height: 22, borderRadius: "50%", bgcolor: ink }} />
      </Box>
    );
  }

  if (type === "promo") {
    return (
      <Stack spacing={0.8} sx={{ width: "100%" }}>
        <Box sx={{ height: 26, borderRadius: 1.2, bgcolor: ink, opacity: 0.85 }} />
        <Stack direction="row" spacing={0.8}><Box sx={card} /><Box sx={card} /><Box sx={card} /></Stack>
      </Stack>
    );
  }

  if (type === "logos") {
    return (
      <Stack direction="row" spacing={0.8} sx={{ width: "100%", alignItems: "center", justifyContent: "center" }}>
        {[0, 1, 2, 3].map((item) => <Box key={item} sx={{ width: 23, height: 23, borderRadius: "50%", bgcolor: item % 2 ? soft : ink, opacity: item % 2 ? 1 : 0.55 }} />)}
      </Stack>
    );
  }

  return (
    <Stack spacing={0.8} sx={{ width: "100%" }}>
      <Box sx={{ ...line, width: type === "benefits" ? "48%" : "62%", height: 8 }} />
      <Stack direction="row" spacing={0.8}>
        {[0, 1, 2].map((item) => <Box key={item} sx={{ ...card, height: type === "categories" ? 28 : 38 }} />)}
      </Stack>
    </Stack>
  );
}

function SegmentChoiceCard({ definition, active, disabled, onToggle }) {
  const presentation = definition.presentation || {};
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "88px minmax(0,1fr)", sm: "112px minmax(0,1fr) auto" },
        gap: { xs: 1.4, sm: 1.8 },
        alignItems: "center",
        p: 1.5,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: active ? "rgba(243,90,58,.38)" : "rgba(25,36,47,.12)",
        bgcolor: active ? "rgba(243,90,58,.035)" : "#fff",
        opacity: disabled ? 0.62 : 1,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          height: 74,
          px: 1.15,
          py: 1.1,
          borderRadius: 2,
          bgcolor: "#F7F8FA",
          border: "1px solid rgba(25,36,47,.08)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <SegmentMiniPreview type={presentation.preview} active={active} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center">
          <Typography sx={{ fontWeight: 950, color: "#19242F" }}>{definition.label}</Typography>
          <Chip
            size="small"
            icon={active ? <CheckRoundedIcon /> : undefined}
            label={active ? "En la página" : "Disponible"}
            variant={active ? "filled" : "outlined"}
            sx={{ height: 23, fontWeight: 800 }}
          />
        </Stack>
        <Typography variant="body2" sx={{ mt: 0.45, color: "rgba(25,36,47,.72)" }}>
          {presentation.summary}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mt: 0.55, color: "rgba(25,36,47,.52)", lineHeight: 1.35 }}>
          {presentation.placement}
        </Typography>
      </Box>

      <Button
        size="small"
        variant={active ? "outlined" : "contained"}
        startIcon={active ? <RemoveRoundedIcon /> : <AddRoundedIcon />}
        disabled={disabled}
        onClick={() => onToggle(!active)}
        sx={{
          gridColumn: { xs: "1 / -1", sm: "auto" },
          justifySelf: { xs: "stretch", sm: "end" },
          minWidth: { sm: 112 },
          textTransform: "none",
          fontWeight: 900,
        }}
      >
        {active ? "Quitar" : "Agregar"}
      </Button>
    </Box>
  );
}

export default function LandingSettingsDialog({
  open,
  form,
  productsEnabled = true,
  saving = false,
  changingPublicStatus = false,
  initialTab = 0,
  showTrialGuide = false,
  onClose,
  onSave,
  onChangePublicStatus,
  onUploadAsset,
  onDeleteAsset,
}) {
  const compactViewport = useMediaQuery("(max-width:700px)");
  const [draft, setDraft] = React.useState(() => clone(form || {}));
  const [tab, setTab] = React.useState(initialTab);
  const [error, setError] = React.useState("");
  const [pendingPublicStatus, setPendingPublicStatus] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setDraft(clone(form || {}));
      setTab(Number(initialTab) || 0);
      setError("");
      setPendingPublicStatus("");
    }
  }, [form, initialTab, open]);

  const change = (path, value) => setDraft((current) => setAtPath(current, path, value));
  const changeMany = React.useCallback((changes) => {
    setDraft((current) =>
      Object.entries(changes || {}).reduce(
        (next, [path, value]) => setAtPath(next, path, value),
        current
      )
    );
  }, []);

  const segmentDefinitions = React.useMemo(
    () => SEGMENT_ORDER.map((segmentType) => getSegmentDefinition(segmentType)).filter(Boolean),
    []
  );

  const upload = async (path, file, metadata, options = {}) => {
    const asset = await onUploadAsset?.(file, metadata, options);
    const value = asset?.value || asset?.publicPath || asset?.url || "";
    if (!value) throw new Error("No se recibió la ruta del archivo subido");
    change(path, value);
  };

  const remove = async (path) => {
    const current = getAtPath(draft, path, "");
    if (current) await onDeleteAsset?.(current);
    change(path, "");
  };

  const currentPublicStatus = normalizePublicStatus(form?.publicStatus);
  const selectedPublicStatus = normalizePublicStatus(draft?.publicStatus);
  const publicStatusChanged = selectedPublicStatus !== currentPublicStatus;
  const contentChanged = React.useMemo(
    () =>
      JSON.stringify(withoutPublicStatus(draft)) !==
      JSON.stringify(withoutPublicStatus(form)),
    [draft, form]
  );
  const busy = saving || changingPublicStatus;

  const persist = async (nextPublicStatus = "") => {
    try {
      setError("");

      if (contentChanged) {
        await onSave?.(
          { ...draft, publicStatus: currentPublicStatus },
          {
            editorKey: `settings:${tab}`,
            editorType: "settings",
            tabIndex: tab,
            sectionLabel: TABS[tab] || "Ajustes globales",
          }
        );
      }

      if (nextPublicStatus) {
        const result = await onChangePublicStatus?.(nextPublicStatus);
        if (!result) {
          throw new Error("No se pudo confirmar el cambio de estado público.");
        }
      }

      setPendingPublicStatus("");
      onClose?.();
    } catch (err) {
      setError(err?.message || "No se pudieron guardar los ajustes");
    }
  };

  const save = async () => {
    if (publicStatusChanged) {
      setPendingPublicStatus(selectedPublicStatus);
      return;
    }
    await persist();
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      fullScreen={compactViewport}
      maxWidth="md"
    >
      <DialogTitle sx={{ pb: 0, fontWeight: 950, color: "#19242F" }}>Configuración de la página</DialogTitle>
      <Box sx={{ px: { xs: 0.75, sm: 2.5 }, pt: 1 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
          {TABS.map((label) => <Tab key={label} label={label} sx={{ textTransform: "none", fontWeight: 850 }} />)}
        </Tabs>
      </Box>
      <DialogContent dividers>
        <Stack spacing={2.2}>
          {showTrialGuide ? (
            <Alert severity="info">Completá primero identidad, portada, contacto y publicación. Las imágenes avanzadas pueden agregarse después.</Alert>
          ) : null}

          {tab === 0 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 950 }}>Paleta general</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.primaryColor" label="Color principal" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.secondaryColor" label="Color oscuro" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.surfaceColor" label="Superficie" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.backgroundColor" label="Fondo general" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.textColor" label="Texto principal" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.mutedTextColor" label="Texto secundario" onChange={change} /></Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.headingFontFamily" label="Tipografía de títulos" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.bodyFontFamily" label="Tipografía de textos" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.googleFontsUrl" label="URL de Google Fonts" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.borderRadius" label="Redondeo general" type="number" onChange={(path, value) => change(path, Number(value) || 0)} /></Grid>
              </Grid>
            </Stack>
          ) : null}

          {tab === 1 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.logoUrl", "")}
                  label="Logo principal"
                  helperText="SVG, PNG, WebP o JPG."
                  disabled={saving}
                  onUpload={(file, options) => upload("theme.logoUrl", file, { sectionKey: "branding", assetKey: "logo" }, options)}
                  onRemove={() => remove("theme.logoUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.compactLogoUrl", "")}
                  label="Logo compacto"
                  helperText="Versión cuadrada o reducida para celular."
                  disabled={saving}
                  onUpload={(file, options) => upload("theme.compactLogoUrl", file, { sectionKey: "branding", assetKey: "compact-logo" }, options)}
                  onRemove={() => remove("theme.compactLogoUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.faviconUrl", "")}
                  label="Favicon"
                  helperText="Ícono cuadrado para la pestaña."
                  disabled={saving}
                  accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                  onUpload={(file, options) => upload("theme.faviconUrl", file, { sectionKey: "branding", assetKey: "favicon" }, options)}
                  onRemove={() => remove("theme.faviconUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "hero.imageUrl", "")}
                  label="Imagen principal de portada"
                  helperText="Se usa cuando la portada no tiene slider."
                  disabled={saving}
                  onUpload={(file, options) => upload("hero.imageUrl", file, { sectionKey: "hero", assetKey: "background" }, options)}
                  onRemove={() => remove("hero.imageUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "about.imageUrl", "")}
                  label="Imagen sobre el negocio"
                  disabled={saving}
                  onUpload={(file, options) => upload("about.imageUrl", file, { sectionKey: "about", assetKey: "main" }, options)}
                  onRemove={() => remove("about.imageUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "seo.imageUrl", "")}
                  label="Imagen para compartir"
                  helperText="Vista previa para redes sociales y mensajería."
                  disabled={saving}
                  onUpload={(file, options) => upload("seo.imageUrl", file, { sectionKey: "seo", assetKey: "social" }, options)}
                  onRemove={() => remove("seo.imageUrl")}
                />
              </Grid>
            </Grid>
          ) : null}

          {tab === 2 ? (
            <Stack spacing={2.4}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F" }}>
                  Elegí qué querés sumar a la página
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.45, color: "rgba(25,36,47,.62)" }}>
                  Cada opción usa una sección que ya existe en el WebBuilder. Podés agregarla ahora y editar su contenido después desde la página.
                </Typography>
              </Box>

              {!productsEnabled ? (
                <Alert severity="info">
                  Los bloques de productos y servicios aparecen como referencia, pero sólo se pueden agregar cuando ese módulo está habilitado en el proyecto.
                </Alert>
              ) : null}

              {SECTION_GROUPS.map((group) => {
                const definitions = segmentDefinitions.filter(
                  (definition) => definition.presentation?.group === group.id
                );
                if (!definitions.length) return null;
                const commercialGroup = group.id === "commerce";
                const commercialEnabled = getAtPath(draft, "sections.showProducts", true) !== false;

                return (
                  <Box key={group.id}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      sx={{ mb: 1.15 }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: "#19242F" }}>{group.title}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)" }}>
                          {group.description}
                        </Typography>
                      </Box>

                      {commercialGroup ? (
                        <FormControlLabel
                          sx={{ m: 0, mr: { sm: 0.5 } }}
                          control={
                            <Switch
                              size="small"
                              disabled={!productsEnabled}
                              checked={commercialEnabled}
                              onChange={(event) => change("sections.showProducts", event.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 850 }}>
                              {commercialEnabled ? "Zona comercial activa" : "Zona comercial oculta"}
                            </Typography>
                          }
                        />
                      ) : null}
                    </Stack>

                    <Stack spacing={1.15}>
                      {definitions.map((definition) => {
                        const ownVisible = definition.visibilityPath
                          ? getAtPath(draft, definition.visibilityPath, definition.defaultVisible) !== false
                          : true;
                        const active = definition.requiresProducts
                          ? productsEnabled && commercialEnabled && ownVisible
                          : ownVisible;
                        const disabled = definition.requiresProducts && !productsEnabled;

                        return (
                          <SegmentChoiceCard
                            key={definition.id}
                            definition={definition}
                            active={active}
                            disabled={disabled}
                            onToggle={(nextVisible) =>
                              changeMany(buildSegmentVisibilityChanges(definition.id, nextVisible))
                            }
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : null}

          {tab === 3 ? (
            <Grid container spacing={2}>
              <Grid item xs={12}><Field form={draft} path="business.name" label="Nombre del negocio" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.shortDescription" label="Descripción breve" onChange={change} multiline minRows={3} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.whatsapp" label="WhatsApp" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.phone" label="Teléfono" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.email" label="Correo electrónico" type="email" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.address" label="Dirección" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.city" label="Ciudad" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.province" label="Provincia" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.country" label="País" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.schedule" label="Horarios" onChange={change} multiline minRows={2} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.mapEmbedUrl" label="URL de mapa embebido" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.instagram" label="Instagram" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.facebook" label="Facebook" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.linkedin" label="LinkedIn" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.website" label="Sitio externo" onChange={change} /></Grid>
            </Grid>
          ) : null}

          {tab === 4 ? (
            <Stack spacing={2}>
              <FormControl fullWidth disabled={busy}>
                <InputLabel>Estado público</InputLabel>
                <Select
                  label="Estado público"
                  value={selectedPublicStatus}
                  onChange={(event) => {
                    change("publicStatus", event.target.value);
                    setPendingPublicStatus("");
                  }}
                  inputProps={{ "aria-describedby": "public-status-help" }}
                >
                  <MenuItem value="draft">Borrador</MenuItem>
                  <MenuItem value="published">Publicado</MenuItem>
                  <MenuItem value="maintenance">Mantenimiento</MenuItem>
                </Select>
              </FormControl>
              <Typography id="public-status-help" variant="body2" sx={{ color: "text.secondary" }}>
                Estado actual del servidor: <strong>{publicStatusLabel(currentPublicStatus)}</strong>.
                La selección no modifica la web hasta que confirmes la transición.
              </Typography>
              <Field form={draft} path="seo.title" label="Título para Google y redes" onChange={change} />
              <Field form={draft} path="seo.description" label="Descripción para Google y redes" onChange={change} multiline minRows={4} />
              <Field form={draft} path="messages.maintenanceTitle" label="Título de mantenimiento" onChange={change} />
              <Field form={draft} path="messages.maintenanceDescription" label="Mensaje de mantenimiento" onChange={change} multiline minRows={3} />
              <Alert severity={selectedPublicStatus === "published" ? "success" : "warning"}>
                {selectedPublicStatus === "published"
                  ? "Publicado toma siempre el último borrador guardado y crea una revisión pública real."
                  : selectedPublicStatus === "maintenance"
                    ? "Mantenimiento conserva la última publicación y muestra solamente el aviso público."
                    : "Borrador oculta la web pública sin borrar publicaciones ni revisiones anteriores."}
              </Alert>
              {pendingPublicStatus ? (
                <Alert severity="info" role="alert">
                  <Typography sx={{ fontWeight: 900 }}>
                    Confirmar cambio a {publicStatusLabel(pendingPublicStatus)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {statusConfirmationMessage(pendingPublicStatus)}
                  </Typography>
                </Alert>
              ) : null}
            </Stack>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.25, sm: 2 },
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
        }}
      >
        {pendingPublicStatus ? (
          <>
            <Button
              onClick={() => setPendingPublicStatus("")}
              disabled={busy}
              sx={{ textTransform: "none", fontWeight: 850, width: { xs: "100%", sm: "auto" } }}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              onClick={() => persist(pendingPublicStatus)}
              disabled={busy}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A", width: { xs: "100%", sm: "auto" } }}
            >
              {busy ? "Aplicando…" : `Confirmar ${publicStatusLabel(pendingPublicStatus)}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 850, width: { xs: "100%", sm: "auto" } }}>Cancelar</Button>
            <Button variant="contained" onClick={save} disabled={busy} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A", width: { xs: "100%", sm: "auto" } }}>
              {busy ? "Guardando…" : publicStatusChanged ? "Revisar transición" : "Guardar configuración"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
